import React from 'react';
import { Check, Lock } from 'lucide-react';

type StepState = 'active' | 'completed' | 'locked';

function getStepState(index: number, activeIndex: number, visibleCount: number): StepState {
  if (index >= visibleCount) return 'locked';
  if (index === activeIndex) return 'active';
  return 'completed';
}

interface StepperTrackerProps {
  steps: { label: string }[];
  activeStepIndex: number;
  visibleCount: number;
  onStepClick?: (index: number) => void;
  onLockedStepClick?: () => void;
  onStepKeyDown?: (e: React.KeyboardEvent, index: number) => void;
  stepIdPrefix: string;
  ariaLabel: string;
  interactive?: boolean;
}

export const StepperTracker: React.FC<StepperTrackerProps> = ({
  steps,
  activeStepIndex,
  visibleCount,
  onStepClick,
  onLockedStepClick,
  onStepKeyDown,
  stepIdPrefix,
  ariaLabel,
  interactive = true,
}) => {
  const totalSteps = steps.length;

  return (
    <div
      className="flex items-start mb-5"
      role={interactive ? 'tablist' : 'list'}
      aria-label={ariaLabel}
    >
      {steps.map((step, index) => {
        const state = getStepState(index, activeStepIndex, visibleCount);
        const isLast = index === totalSteps - 1;

        const stepLabel = `${step.label}${state === 'locked' ? ' (locked)' : state === 'completed' ? ' (completed)' : ' (active)'}`;

        const stepContent = (
          <>
            <StepCircle state={state} index={index} />
            <span
              className="text-[clamp(0.6rem,1.2vw,0.6875rem)] leading-tight text-center font-sans"
              style={{
                fontWeight: state === 'active' ? 600 : 500,
                color: state === 'active'
                  ? 'var(--clue-text-primary)'
                  : state === 'completed'
                    ? 'var(--clue-green-accent)'
                    : 'var(--clue-text-tertiary)',
              }}
            >
              {step.label}
            </span>
          </>
        );

        return (
          <React.Fragment key={index}>
            {interactive ? (
              <button
                id={`${stepIdPrefix}-${index}`}
                role="tab"
                aria-selected={state === 'active'}
                aria-disabled={state === 'locked'}
                aria-label={stepLabel}
                tabIndex={state === 'active' ? 0 : -1}
                onClick={() => {
                  if (state === 'locked') {
                    onLockedStepClick?.();
                  } else {
                    onStepClick?.(index);
                  }
                }}
                onKeyDown={(e) => onStepKeyDown?.(e, index)}
                className="flex flex-col items-center gap-1.5 cursor-pointer shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                style={{ minWidth: 48 }}
              >
                {stepContent}
              </button>
            ) : (
              <div
                id={`${stepIdPrefix}-${index}`}
                role="listitem"
                aria-label={stepLabel}
                className="flex flex-col items-center gap-1.5 shrink-0"
                style={{ minWidth: 48 }}
              >
                {stepContent}
              </div>
            )}

            {!isLast && (
              <div className="flex-1 flex items-center px-1" style={{ marginBottom: '26px', paddingTop: '19px' }}>
                <div
                  className="w-full rounded-full transition-colors duration-300"
                  style={{
                    height: '2px',
                    backgroundColor: index + 1 < visibleCount
                      ? 'var(--clue-green-soft)'
                      : 'var(--clue-border-light)',
                  }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const StepCircle: React.FC<{ state: StepState; index: number }> = ({ state, index }) => {
  if (state === 'active') {
    return (
      <div className="relative flex items-center justify-center">
        <div
          className="absolute w-[46px] h-[46px] rounded-full"
          style={{
            border: '2px solid var(--clue-green-accent)',
            opacity: 0.4,
          }}
        />
        <div
          className="w-[38px] h-[38px] rounded-full flex items-center justify-center"
          style={{
            backgroundColor: 'var(--clue-text-primary)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <span className="font-mono text-[clamp(0.75rem,1.4vw,0.875rem)] font-semibold text-white dark:text-black">{index + 1}</span>
        </div>
      </div>
    );
  }

  if (state === 'completed') {
    return (
      <div
        className="w-[38px] h-[38px] rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--clue-green-accent)' }}
      >
        <Check className="w-4.5 h-4.5 text-white" strokeWidth={3} />
      </div>
    );
  }

  // locked
  return (
    <div
      className="w-[38px] h-[38px] rounded-full flex items-center justify-center"
      style={{
        backgroundColor: 'var(--clue-bg-secondary)',
        border: '1.5px solid var(--clue-border-light)',
      }}
    >
      <Lock className="w-3.5 h-3.5" style={{ color: 'var(--clue-text-locked)' }} />
    </div>
  );
};

export default StepperTracker;
